import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Personalised pro claim short-link resolver.
 *
 * URL: /p/:code  e.g. https://shopperden.com/p/x4k2lm
 *
 * Mounts → calls /api/leads/p/:code (stamps the click attribution
 * server-side) → redirects the visitor to /claim/{place_id} where
 * their business name + city are pre-filled.
 *
 * Why this exists: the approved Meta WhatsApp template has a STATIC
 * URL button — we can't pass per-pro params via the button. Embedding
 * `shopperden.com/p/<code>` inside the WhatsApp message body works
 * because WhatsApp auto-detects URLs in body text and makes them
 * tappable, giving us full per-pro tracking + a personalised landing.
 */
const ShortClaimRedirect = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/leads/p/${encodeURIComponent(code)}`,
          { timeout: 8000 });
        if (cancelled) return;
        const placeId = r.data?.place_id;
        if (placeId) {
          // window.location.replace so the /p/<code> URL is dropped
          // from history — clean back-button behaviour.
          window.location.replace(`/claim/${encodeURIComponent(placeId)}`);
          return;
        }
        setError('not_found');
        // Safety: if the API returns 200 but no place_id, still bounce home.
        setTimeout(() => navigate('/', { replace: true }), 800);
      } catch (e) {
        if (cancelled) return;
        // Unknown / expired shortcode → friendly redirect to homepage
        // rather than a 404. Marketing taps must never see a broken page.
        setError(e?.response?.status === 404 ? 'not_found' : 'network');
        setTimeout(() => navigate('/', { replace: true }), 1200);
      }
    })();
    return () => { cancelled = true; };
  }, [code, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A]" data-testid="short-claim-redirect">
      <div className="text-center text-white/80 max-w-sm px-6">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-[#D4AF37]" />
        <p className="text-sm tracking-widest uppercase">
          {error ? 'Redirecting…' : 'Loading your personalised profile…'}
        </p>
        {error === 'not_found' && (
          <p className="text-xs text-white/50 mt-3">
            This link has expired. Sending you to the homepage.
          </p>
        )}
      </div>
    </div>
  );
};

export default ShortClaimRedirect;
